import React from 'react'
import { path } from 'ramda'
import { Helmet } from 'react-helmet-async'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import type { RouteComponentProps } from 'react-router'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from '@/hooks/redux'
import SuiteInfo from '@/features/Listing/suite-info'
import { H5 } from '@/components/text'
import { Box } from '@/components/structure'
import { DefaultButtonBar } from '@/components/button-bar'
import { Button } from '@/components/button'
import { breakpoints } from '@/styles'
import {
  flattenPadInfo,
  AgreementForm,
  useAgreementForm,
  showConnectPadSuccessModal,
  useConnectPadErrorModal
} from '@/features/Pad'
import useSuiteInfo from '@/hooks/get-suite-info'
import useGetPadInfo from '@/hooks/use-get-pad-info'
import Panel from '@/components/panel'
import { PaymentData } from '@/features/Payments/types'
import usePayment from './use-payment'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const ContentWrapper = styled.div`
  max-width: 800px;
  width: calc(100% - 40px);
  margin: 0 20px 100px;
  text-align: left;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: calc(100% - 20px);
    margin: 0 10px 70px;
  }
`

type RouteParams = {
  listingId: string
  paymentId: string
}

const PadReviewRequest: React.FC<RouteComponentProps<RouteParams>> = ({
  match: {
    params: { listingId, paymentId }
  }
}) => {
  const { show: showErrorModal } = useConnectPadErrorModal()
  const paymentData: PaymentData = usePayment({ listingId, paymentId })
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const suite = useSuiteInfo(listingId)
  const contractId = useSelector(path(['listing', listingId, 'contract', 'listing_contract_id']))
  const { data: pad } = useGetPadInfo()
  const { data, isValid, current: contract, mutate } = useAgreementForm([suite])

  if (!paymentData || !pad) return null
  const padInfo = flattenPadInfo(pad)
  const onClick = async () => {
    const response = await dispatch.settlements.signPadRequest({
      signature: data?.signature,
      paymentId: paymentId,
      contractId
    })

    if (response.ok) {
      history.push(`/rental/${listingId}/payments`)
      showConnectPadSuccessModal({ contracts: [suite], paymentIds: [paymentId] })
    } else {
      showErrorModal()
    }
  }
  return (
    <>
      <Helmet>
        <title>{t('b.payments.view.payments.title', 'Payments')}</title>
      </Helmet>
      <SuiteInfo listingId={listingId} />
      <Wrapper>
        <Box top={40} bottom={24}>
          <H5>{t('b.payment.view.pad_payment_requests.label', 'PAD Payment Requests')}</H5>
        </Box>
        <ContentWrapper>
          <Panel hSpace={32} vSpace={32}>
            <AgreementForm
              contract={contract}
              data={data}
              mutate={mutate}
              paymentData={paymentData}
              values={padInfo}
              isModal
            />
          </Panel>
        </ContentWrapper>
      </Wrapper>
      <DefaultButtonBar>
        <Button size="small" disabled={!isValid} onClick={onClick}>
          {t('l.payment_settings.view.authorize_payments.button', 'Authorize Payments')}
        </Button>
      </DefaultButtonBar>
    </>
  )
}

export default PadReviewRequest
